/**
 * @file PlanPricesPreview.tsx
 * @brief DEV ONLY: Super Admin → Billing → Plans & seats with seeded
 *        GET /api/admin/plans data (`/__preview/plan-prices`).
 *
 * @details
 * Checks the four plan cards and the "Will change" list without a signed-in
 * account. Look, don't press Save: it calls the real settings API (no token,
 * so it fails), and the local backend writes the PROD DB.
 * Mounted only when `import.meta.env.DEV` (routes/index.tsx).
 *
 * @author DES (prepared with Claude)
 * @date 21-10-2026
 * @updated 22-10-2026 — seeded from the B3 /plans shape (W6)
 * @updated 22-10-2026 — wider frame so the four cards sit side by side (W8)
 * @updated 24-10-2026 — `included` + interpretation minimums (W18); `?pool=off`
 *   turns the Notes pool off, `?old=1` drops the W18 fields (backend not live)
 * @updated 24-10-2026 — W19: Free pay-as-you-go Meeting Notes rate + minimum
 * @updated 24-10-2026 — B20 / W17: Free reset mode + expiry, every plan
 *   number, `rule` per setting and a stubbed impact count (no request is
 *   made). `?old=1` = a backend before B20 (no rule, impact 404 → "–");
 *   `?once=1` = Free already one time with a 30-day expiry.
 * @updated 31-10-2026 — W34: `?b43=1` = Free on, one time 300, the B43
 *   settings (FREE_INTEL_MAX_MINUTES, `&intel=120` caps it; FREE_GRACE_MINUTES).
 */

import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { __setPlanImpactStub, billingKeys } from "../api";
import { SubscriptionPlansCard } from "../components/SubscriptionPlansCard";
import type { AdminPlansResponse } from "../schemas";

type S = AdminPlansResponse["plans"][number]["settings"][number];

const params = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
const OLD_BACKEND = params.get("old") === "1";
const POOL_ON = params.get("pool") !== "off";
const B43 = params.get("b43") === "1";
const ONCE = params.get("once") === "1" || B43;
const INTEL_MAX = Number(params.get("intel") ?? 300);

// B20 rules (BACKEND planSettingRules.mjs), by key; none on an old backend.
function ruleFor(key: string): S["rule"] {
  if (OLD_BACKEND) return undefined;
  const r = (appliesFrom: string, affects: string) => ({ appliesFrom, affects });
  if (/_PRICE_/.test(key)) return r("new_subscriptions", "New subscriptions only. Existing subscribers keep the Stripe price they signed up at.");
  if (/(RATE|MIN_CHARGE)/.test(key)) return r("next_session", "The next session or recording; a running one keeps its start rate.");
  if (/SEATS$/.test(key)) return r("seats", "Nobody loses a seat; existing companies keep theirs.");
  if (key === "note_max_duration_seconds") return r("next_recording", "The next voice note.");
  if (/^FREE_PLAN_(ENABLED|RESET_MODE)$|_ENABLED$/.test(key)) return r("now", "At once.");
  if (/EXPIRY_DAYS|FREE_INTERPRET_MINUTES/.test(key)) return r("up_now_down_new_signups", "Longer: at once. Shorter: new sign-ups only.");
  return r("up_now_down_next_period", "Up: at once. Down: from each user's next period; never mid-month.");
}

const s = (
  key: string,
  label: string,
  kind: S["kind"],
  effective: number | boolean | string,
  extra: Partial<S> = {},
): S => ({ key, label, kind, value: String(effective), effective, rule: ruleFor(key), ...extra });

// Stubbed impact counts (no request): what GET /api/admin/plans/impact sends.
if (!OLD_BACKEND) {
  __setPlanImpactStub((key, value) => {
    const counts: Record<string, number | null> = {};
    let note: string | null = null;
    if (key.startsWith("FREE_")) {
      counts.users = 1204;
      if (key === "FREE_PLAN_NOTES_MINUTES") {
        counts.usersAlreadyOver = 37;
        counts.usersOverInTotal = 41;
      }
      if (key === "FREE_PLAN_RESET_MODE" && value === "one_time") {
        counts.usersAlreadyUsedTotal = 9;
        note = "With one-time Free, 600 minutes is the total for life: users already at it get no more.";
      }
    } else if (/_PRICE_/.test(key)) {
      counts.subscriptions = 86;
      counts.companies = key.includes("BUSINESS") ? 7 : 0;
      counts.affectedNow = 0;
    } else if (/SEATS$/.test(key)) {
      counts.companies = 7;
      counts.companiesOutside = 1;
    } else if (key === "note_max_duration_seconds") {
      counts.notesLast30DaysLonger = 4;
    } else if (/(RATE|MIN_CHARGE)/.test(key)) {
      counts.users = key.includes("BUSINESS") ? 52 : 86;
      counts.runningNow = 2;
    } else {
      counts.users = key.includes("BUSINESS") ? 52 : 86;
      counts.companies = key.includes("BUSINESS") ? 7 : 0;
    }
    return { status: "ok", data: { key, current: null, proposed: value, counts, note } };
  });
}

// Stub numbers for the preview only (launch prices as on prod 18-10-2026).
function paid(code: "PRO" | "BUSINESS", name: string, perSeat: string) {
  const prices: Record<string, [number, number]> = {
    PRO_GBP: [1499, 14900],
    PRO_USD: [1899, 18900],
    BUSINESS_GBP: [2299, 22900],
    BUSINESS_USD: [2799, 27900],
  };
  const out: S[] = [];
  for (const cur of ["GBP", "USD"] as const) {
    const [m, y] = prices[`${code}_${cur}`];
    out.push(s(`PLAN_${code}_PRICE_${cur}_MINOR`, `${name} price a month${perSeat}`, "minor", m, { currency: cur, interval: "month" }));
    out.push(s(`PLAN_${code}_PRICE_${cur}_ANNUAL_MINOR`, `${name} price a year${perSeat}`, "minor", y, { currency: cur, interval: "year" }));
  }
  if (code === "BUSINESS") {
    out.push(s("PLAN_BUSINESS_MIN_SEATS", "Minimum seats", "seats", 2));
    if (!OLD_BACKEND) out.push(s("PLAN_BUSINESS_MAX_SEATS", "Maximum seats (0 = no maximum)", "seats", 0, { value: null }));
    out.push(s("PLAN_BUSINESS_SOFT_CAP_MINUTES", "Intelligence minutes per seat a month (pooled)", "minutes", 1200));
    out.push(s("PLAN_BUSINESS_SEARCH_LIMIT", "Ask AI questions a month", "limit", -1));
  } else {
    out.push(s("PLAN_PRO_INCLUDED_MINUTES", "Intelligence minutes a month", "minutes", 600, { value: null }));
    out.push(s("PLAN_PRO_SEARCH_LIMIT", "Ask AI questions a month", "limit", 300));
  }
  if (!OLD_BACKEND) {
    const P = `PLAN_${code}`;
    out.push(s(`${P}_NOTES_MINUTES`, "Meeting Notes minutes a month (-1 = unlimited, fair use)", "limit", -1, { value: null }));
    out.push(s(`${P}_NOTES_FAIR_USE_MINUTES`, "Meeting Notes fair use, minutes a month (0 = shared)", "minutes", 0, { value: null }));
    out.push(s(`${P}_HISTORY_DAYS`, "Days of history (0 = unlimited)", "days", 0, { value: null }));
  }
  for (const cur of ["GBP", "USD"] as const) {
    out.push(s(`PLAN_${code}_S2S_RATE_${cur}_MINOR`, "Interpretation per minute", "minor", cur === "GBP" ? 40 : 50, { currency: cur }));
    out.push(s(`PLAN_${code}_REALTIME_RATE_${cur}_MINOR`, "Realtime interpretation per minute", "minor", cur === "GBP" ? 55 : 70, { currency: cur }));
    out.push(s(`PLAN_${code}_BOARDROOM_RATE_${cur}_MINOR`, "Extra Intelligence per minute", "minor", 1, { currency: cur }));
    if (!OLD_BACKEND) {
      out.push(s(`PLAN_${code}_MIN_CHARGE_${cur}_MINOR`, "Minimum per interpretation session (empty = platform)", "minor", 200, { currency: cur, value: null }));
      out.push(s(`PLAN_${code}_MIN_CHARGE_REALTIME_${cur}_MINOR`, "Minimum per realtime session (empty = platform)", "minor", 100, { currency: cur, value: null }));
    }
  }
  return out;
}

const rates = {
  interpretationMinor: 60,
  realtimeMinor: 75,
  extraIntelligenceMinor: 1.2,
  minChargeMinor: 100,
  ...(OLD_BACKEND ? {} : { interpretationMinMinor: 200, realtimeMinMinor: 100 }),
  // B23 (W19): pay-as-you-go Meeting Notes, 5p / min + 50p minimum (ensure_payg_5p_pricing values).
  ...(OLD_BACKEND ? {} : { noteMinor: 5, noteMinChargeMinor: 50 }),
};
const paidRates = { ...rates, minChargeMinor: null };
const paidIncluded = OLD_BACKEND
  ? {}
  : {
      included: {
        notesMinutes: null,
        notesFairUseMinutes: 3600,
        notesPoolOn: POOL_ON,
        historyDays: null,
        interpretationMinutes: 0,
        interpretationOnce: false,
      },
    };

const SEEDED_PLANS: AdminPlansResponse = {
  success: true,
  currencies: ["GBP", "USD"],
  plans: [
    {
      code: "free",
      name: "Free",
      enabled: B43,
      settings: [
        s("FREE_PLAN_ENABLED", "Free plan on", "switch", B43),
        ...(OLD_BACKEND
          ? []
          : [s("FREE_PLAN_RESET_MODE", "Free resets", "choice", ONCE ? "one_time" : "monthly", { options: ["monthly", "one_time"], ...(ONCE ? {} : { value: null }) })]),
        s("FREE_PLAN_NOTES_MINUTES", ONCE ? "Meeting Notes minutes in total" : "Meeting Notes minutes a month", "minutes", ONCE ? 300 : 600),
        s("FREE_PLAN_ASK_LIMIT", ONCE ? "Ask AI questions in total" : "Ask AI questions a month", "count", 10),
        ...(B43
          ? [
              s("FREE_INTEL_MAX_MINUTES", "Most of the free minutes Full Intelligence may use", "minutes", INTEL_MAX),
              s("FREE_GRACE_MINUTES", "A live recording keeps going after the free minutes, minutes", "minutes", 15),
            ]
          : []),
        ...(OLD_BACKEND
          ? []
          : [s("FREE_PLAN_EXPIRY_DAYS", "Free ends after, days from sign-up (0 = never; one-time only)", "days", ONCE ? 30 : 0, ONCE ? {} : { value: null })]),
        s("FREE_PLAN_HISTORY_DAYS", "Days of history (0 = all)", "days", 30),
        s("FREE_INTERPRET_ENABLED", "One-time interpretation minutes on", "switch", true),
        s("FREE_INTERPRET_MINUTES", "One-time interpretation minutes", "minutes", 15, { value: null }),
      ],
      rates: { GBP: rates, USD: rates },
      ...(OLD_BACKEND
        ? {}
        : {
            included: {
              notesMinutes: ONCE ? 300 : 600,
              notesFairUseMinutes: null,
              notesPoolOn: POOL_ON,
              historyDays: 30,
              interpretationMinutes: 15,
              interpretationOnce: true,
            },
          }),
    },
    { code: "pro", name: "Pro", enabled: true, settings: paid("PRO", "Pro", ""), rates: { GBP: paidRates, USD: paidRates }, ...paidIncluded },
    { code: "business", name: "Business", enabled: true, settings: paid("BUSINESS", "Business", " per seat"), rates: { GBP: paidRates, USD: paidRates }, ...paidIncluded },
    { code: "enterprise", name: "Enterprise", enabled: true, contactSales: true, settings: [], rates: null },
  ],
  shared: {
    settings: [
      s("NOTES_FAIR_USE_MINUTES", "Meeting Notes fair use on a plan, minutes a month", "minutes", 3600),
      s("SUBSCRIPTION_ANNUAL_ENABLED", "Yearly plans on", "switch", true),
      ...(OLD_BACKEND ? [] : [s("note_max_duration_seconds", "Voice note: longest recording, seconds", "seconds", 420)]),
      s("RATE_BOARDROOM_PER_MINUTE_GBP_MINOR", "No plan: extra Intelligence per minute", "minor", 1.2, { currency: "GBP" }),
      s("MIN_CHARGE_BOARDROOM_GBP_MINOR", "No plan: minimum per Intelligence meeting", "minor", 100, { currency: "GBP" }),
      s("RATE_BOARDROOM_PER_MINUTE_USD_MINOR", "No plan: extra Intelligence per minute", "minor", 1.5, { currency: "USD" }),
      s("MIN_CHARGE_BOARDROOM_USD_MINOR", "No plan: minimum per Intelligence meeting", "minor", 100, { currency: "USD" }),
    ],
    noPlan: { GBP: rates, USD: rates },
  },
};

export function PlanPricesPreviewPage() {
  const [client] = useState(() => {
    const qc = new QueryClient({
      defaultOptions: {
        queries: { staleTime: Infinity, retry: false, refetchOnMount: false, refetchOnWindowFocus: false },
      },
    });
    qc.setQueryData(billingKeys.plans(), SEEDED_PLANS);
    qc.setQueryData(billingKeys.settings(), {
      settings: {
        SUBSCRIPTION_ENABLED: { value: "true" },
        NOTES_POOL_ENABLED: { value: POOL_ON ? "true" : "false" },
        SEARCH_ENABLED: { value: "true" },
        SEARCH_ENFORCE_LIMITS: { value: "false" },
        RATE_NOTE_PER_MINUTE_GBP_MINOR: { value: "5" },
        RATE_NOTE_PER_MINUTE_USD_MINOR: { value: "5" },
        MIN_CHARGE_NOTE_GBP_MINOR: { value: "50" },
        MIN_CHARGE_NOTE_USD_MINOR: { value: "50" },
      },
    });
    qc.setQueryData(billingKeys.billingCountries(), {
      blocked: [
        { code: "RU", name: "Russia" },
        { code: "BY", name: "Belarus" },
        { code: "IR", name: "Iran" },
      ],
      saved: true,
      defaults: [],
    });
    return qc;
  });
  return (
    <QueryClientProvider client={client}>
      <div className="mx-auto max-w-[1600px] p-4">
        <SubscriptionPlansCard />
      </div>
    </QueryClientProvider>
  );
}
